// =============================================================================
// Global dialog provider — replaces the browser's window.confirm / .prompt /
// .alert (which are ugly on iOS PWAs and sometimes silently no-op) with one
// styled in-app modal.
//
// Hooks:
//   const confirm = useConfirm();
//   const prompt  = usePrompt();
//   const alert   = useAlert();
//
// Usage:
//   if (!(await confirm({ message: 'Delete?', danger: true }))) return;
//   const name = await prompt({ title: 'New group:', placeholder: 'e.g. Home' });
//   await alert({ message: 'Saved.' });
//
// All three resolve a Promise: confirm -> bool, prompt -> string|null,
// alert -> void.
// =============================================================================
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from './i18n';

const DialogCtx = createContext(null);

const useDialogApi = () => {
  const api = useContext(DialogCtx);
  if (!api) throw new Error('Dialog hook used outside <ConfirmProvider>');
  return api;
};

export const useConfirm = () => useDialogApi().confirm;
export const usePrompt = () => useDialogApi().prompt;
export const useAlert = () => useDialogApi().alert;

export const ConfirmProvider = ({ children }) => {
  const { t } = useTranslation();
  // state: { kind: 'confirm'|'prompt'|'alert', options } | null
  const [state, setState] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);
  const resolverRef = useRef(null);

  const open = useCallback((kind, options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      const opts = typeof options === 'string' ? { message: options } : options || {};
      setInputValue(opts.defaultValue || '');
      setState({ kind, options: opts });
    });
  }, []);

  const close = useCallback((value) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setInputValue('');
    if (r) r(value);
  }, []);

  const confirm = useCallback((options) => open('confirm', options), [open]);
  const prompt = useCallback((options) => open('prompt', options), [open]);
  const alert = useCallback((options) => open('alert', options), [open]);

  // Auto-focus the input on prompts.
  useEffect(() => {
    if (state?.kind === 'prompt') {
      const id = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [state]);

  // ESC = cancel, Enter = confirm.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (state.kind === 'prompt') close(null);
        else if (state.kind === 'alert') close();
        else close(false);
      } else if (e.key === 'Enter' && state.kind !== 'prompt') {
        // For prompt we let the form's onSubmit handle Enter so the value
        // is captured; here only handle confirm/alert.
        if (state.kind === 'alert') close();
        else close(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  const api = useMemo(() => ({ confirm, prompt, alert }), [confirm, prompt, alert]);

  const opts = state?.options || {};
  const kind = state?.kind;
  const danger = !!opts.danger;
  const title =
    opts.title ||
    (kind === 'confirm'
      ? t('common.confirm')
      : kind === 'alert'
      ? ''
      : '');
  const message = opts.message || '';
  const cancelText = opts.cancelText || t('common.cancel');
  const confirmText =
    opts.confirmText ||
    (kind === 'confirm'
      ? danger
        ? t('common.delete')
        : t('common.confirm')
      : kind === 'prompt'
      ? t('common.save')
      : 'OK');

  const onCancel = () => {
    if (kind === 'prompt') close(null);
    else if (kind === 'alert') close();
    else close(false);
  };
  const onConfirm = () => {
    if (kind === 'prompt') close(inputValue);
    else if (kind === 'alert') close();
    else close(true);
  };

  return (
    <DialogCtx.Provider value={api}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={onCancel}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                onConfirm();
              }}
            >
              <div className="px-5 pt-5 pb-3">
                {title && (
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                  </h3>
                )}
                {message && (
                  <p
                    className={`text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap ${
                      title ? 'mt-2' : ''
                    }`}
                  >
                    {message}
                  </p>
                )}
                {kind === 'prompt' && (
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={opts.placeholder || ''}
                    className="input mt-3 w-full"
                  />
                )}
              </div>
              <div className="px-5 pb-5 pt-2 flex gap-2 justify-end">
                {kind !== 'alert' && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  type="submit"
                  autoFocus={kind !== 'prompt'}
                  disabled={kind === 'prompt' && !inputValue.trim()}
                  className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                    danger
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-primary-600 hover:bg-primary-700'
                  }`}
                >
                  {confirmText}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DialogCtx.Provider>
  );
};
